import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { SubjectProperty } from '../../models/subject-property.model';
import { EnumsService } from '../../services/enums.service';
import { TransactionDetail } from "../../models/transaction-detail.model";
import { MortgageTerm } from '../../models/mortgage-term.model';
import { LoanPurpose } from '../../models/enums/loan-purpose.enum';
import { LtvValueBasis } from '../../models/enums/ltv-value-basis.enum';
import { ReviewApplicationStep } from '../../models/wizard/review-application.model';
import { ConfigurableFieldHostComponent } from '../configurable-field-host.component';

@Component({
    selector: 'loan-summary',
    templateUrl: 'loan-summary.component.html',
    standalone: false
})
export class LoanSummaryComponent extends ConfigurableFieldHostComponent implements OnInit, OnDestroy {

  private _langChangeSub?: Subscription;

  @Input()
  inEditMode: boolean = false;

  @Input()
  set transactionDetail(transactionDetail: TransactionDetail) {
    this._transactionDetail = transactionDetail;
    this.ltv = this.calculateEstimatedLtv();
  }

  @Input()
  set subjectProperty(subjectProperty: SubjectProperty) {
    this.purposeOfLoan = this._enumsService.loanPurposeName(subjectProperty.purposeOfLoan);
    this._subjectProperty = subjectProperty;
    this.ltv = this.calculateEstimatedLtv();
  }

  @Input()
  set mortgageTerm(mortgageTerm: MortgageTerm) {
    this.mortgageAppliedFor = this._enumsService.mortgageTypeName(mortgageTerm.mortgageAppliedFor);
    this._mortgageTerm = mortgageTerm;
    this.ltv = this.calculateEstimatedLtv();
  }

  @Input()
  set ltvValueBasis(value: LtvValueBasis | undefined) {
    this._ltvValueBasis = value;
    this.ltv = this.calculateEstimatedLtv();
  }

  @Input()
  step: ReviewApplicationStep;

  get subjectProperty(): SubjectProperty {
    return this._subjectProperty;
  }

  get transactionDetail(): TransactionDetail {
    return this._transactionDetail;
  }

  // LLS-218 — appraised value supersedes the estimated (present) value when provided, so the summary
  // shows whichever value the LTV is actually computed from (keeps the displayed value and LTV in sync).
  get hasAppraisedValue(): boolean {
    return !!this._mortgageTerm?.appraisedValue;
  }

  get appraisedValue(): number | undefined {
    return this._mortgageTerm?.appraisedValue;
  }

  @Output()
  loanDetailsClicked: EventEmitter<any> = new EventEmitter<any>();

  purposeOfLoan: string = '';
  mortgageAppliedFor: string = '';
  ltv: number = 0;

  private _subjectProperty: SubjectProperty | null = null;
  private _mortgageTerm: MortgageTerm | null = null;
  private _transactionDetail: TransactionDetail | null = null;
  private _ltvValueBasis: LtvValueBasis | undefined;

  constructor(
    private readonly _enumsService: EnumsService,
    private readonly _transloco: TranslocoService,
  ) {
    super();
  }

  ngOnInit() {
    this.ltv = this.calculateEstimatedLtv();
    // Re-translate cached enum display names when language switches.
    this._langChangeSub = this._transloco.langChanges$.pipe(skip(1)).subscribe(() => {
      if (this._subjectProperty) {
        this.purposeOfLoan = this._enumsService.loanPurposeName(this._subjectProperty.purposeOfLoan);
      }
      if (this._mortgageTerm) {
        this.mortgageAppliedFor = this._enumsService.mortgageTypeName(this._mortgageTerm.mortgageAppliedFor);
      }
    });
  }

  ngOnDestroy(): void {
    this._langChangeSub?.unsubscribe();
  }

  onLoanDetailsClicked = () => {
    this.loanDetailsClicked.emit();
  }

  calculateEstimatedLtv = (): number | undefined => {
    let loanAmount: number | undefined | null = this._mortgageTerm?.amount;
    let estimatedValue: number | undefined | null;
    if (this._subjectProperty.purposeOfLoan === LoanPurpose.Refinance) {
      // Appraised value supersedes the estimated (present) value when provided (LLS-218).
      estimatedValue = this._mortgageTerm?.appraisedValue ?? this._subjectProperty?.presentValue;
    } else if (this._ltvValueBasis === LtvValueBasis.AppraisedValue) {
      estimatedValue = this._mortgageTerm?.appraisedValue || this._subjectProperty?.presentValue;
    } else if (this._ltvValueBasis === LtvValueBasis.PurchasePrice) {
      if (
        this._subjectProperty.purposeOfLoan === LoanPurpose.Purchase &&
        this._transactionDetail?.purchasePriceAmount > 0
      ) {
        estimatedValue = this._transactionDetail.purchasePriceAmount;
      } else {
        estimatedValue = this._mortgageTerm?.appraisedValue || this._subjectProperty?.presentValue;
      }
    } else {
      estimatedValue = this.transactionDetail?.purchasePriceAmount;
    }
    if (!loanAmount) {
      loanAmount = 0;
    }
    if (!estimatedValue) {
      return undefined;
    }
    return Math.round((loanAmount / estimatedValue) * 100);
  }
}
